import {Component, HostListener, Input, OnChanges, OnInit} from '@angular/core';
import {
  ApiMode, Base64UrlDecode,
  CommunicationEntity,
  ConfigService, ConnectMode,
  EventTypes,
  FdkTheme,
  isNativeSdkMode,
  LighthouseBrandListDisplayItem,
  MessageBusEventPayload,
  MessageBusService,
  Organization,
  SDKMode, SearchFilter
} from '@shared-library';
import {FastenService} from './services/fasten.service';
import {ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet} from '@angular/router';
import {NGXLogger} from 'ngx-logger';
import {CommonModule} from "@angular/common";
import {
  getOAuthCallbackContext,
  restoreConfigurationFromOauthContext
} from "./utils/oauth2_callback_context";
import {AuthService, CookieProbeScope} from "./services/auth.service";
import {
  FdkButtonComponent,
  FdkDialogActionsComponent,
  FdkDialogComponent,
  FdkDialogContentComponent,
  FdkDialogTitleComponent,
  FdkFooterComponent,
  FdkHeaderComponent,
  FdkLogoComponent,
} from 'fasten-design-kit';
import {filter} from 'rxjs';
import {
  DEFAULT_EMBED_HEADER_CONFIG,
  EmbedHeaderConfig,
} from './models/embed-header-config';
import {SentryContextService, WidgetExperience} from "./services/sentry-context.service";
import {HealthSystemSearchSkeletonComponent} from './pages/health-system-search/health-system-search-skeleton.component';
import {WelcomeSkeletonComponent} from './pages/welcome/welcome-skeleton.component';

export class SourceListItem {
  brand: LighthouseBrandListDisplayItem
  searchHighlights?: string[]
}


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    FdkButtonComponent,
    FdkDialogActionsComponent,
    FdkDialogComponent,
    FdkDialogContentComponent,
    FdkDialogTitleComponent,
    FdkFooterComponent,
    FdkHeaderComponent,
    FdkLogoComponent,
    HealthSystemSearchSkeletonComponent,
    WelcomeSkeletonComponent,
  ],
})
export class AppComponent implements OnInit, OnChanges {
  readonly testApiMode = ApiMode.Test
  headerConfig: EmbedHeaderConfig = {...DEFAULT_EMBED_HEADER_CONFIG}
  closeConfirmationOpen = false
  private activeHeaderRoute!: ActivatedRoute

  @Input('public-id') publicId = ''; //validate
  @Input('external-id') externalId = ''; //validate
  @Input('email') email = '';
  @Input('external-state') externalState = ''; //validate. External-state can only be set if reconnect-org-connection-id is present.
  @Input('reconnect-org-connection-id') reconnectOrgConnectionId?: string;
  @Input('tefca-mode') tefcaMode?: boolean = false;
  @Input('tefca-csp-prompt-force') tefcaCspPromptForce?: boolean = false;
  @Input('identity-request-uri') identityRequestUri?: string = '';
  @Input('static-backdrop') staticBackdrop?: boolean = false;
  @Input('event-types') eventTypes?: string = ''; //comma-separated list of event types, e.g. "search.query,patient.connection_success"

  @Input('show-splash') showSplash?: boolean = false; //show the splash screen on initial load, defaults to false
  @Input('search-only') searchOnly?: boolean = false;
  @Input('search-query') searchQuery?: string = '';
  @Input('search-sort-by') searchSortBy?: string = ''; //e.g. "location"
  @Input('search-sort-by-opts') searchSortByOpts?: string = ''; //e.g. base64Url encoded "{}" or '{"location":{"zipcodes":["94105"]}}' --
  @Input('brand-id') brandId?: string = '';
  @Input('portal-id') portalId?: string = '';
  @Input('endpoint-id') endpointId?: string = '';
  @Input('sdk-mode') sdkMode?: string = ''
  @Input('connect-mode') connectMode?: string = ''
  @Input('theme') theme: FdkTheme = FdkTheme.Light
  @Input('accent-color') accentColor = ''
  @Input('logo-url') logoUrl = ''
  @Input('company-name') companyName = ''

  //If returning from a callback from the fasten identity service, code and state should be populated, we need to just redirect in this case.
  @Input('code') idpCode?: string = '';
  @Input('state') idpState?: string = '';
  @Input('error') idpError?: string = '';
  @Input('error-description') idpErrorDescription?: string = '';
  @Input('error-uri') idpErrorUri?: string = '';

  populateInputsFromWindowLocation(): void {
    //on initial load, the query string parameters for this embedded page need to be processed, before the router takes over
    let urlParams = new URLSearchParams(window.location.search)
    this.publicId = urlParams.get('public-id') || ''
    this.externalId = urlParams.get('external-id') || ''
    this.email = urlParams.get('email') || ''
    this.externalState = urlParams.get('external-state') || ''
    this.reconnectOrgConnectionId = urlParams.get('reconnect-org-connection-id') || ''
    this.searchOnly = urlParams.get('search-only') == 'true'
    this.tefcaMode = urlParams.get('tefca-mode') == 'true'
    this.tefcaCspPromptForce = urlParams.get('tefca-csp-prompt-force') == 'true'
    this.identityRequestUri = urlParams.get('identity-request-uri') || ''
    this.eventTypes = urlParams.get('event-types') || ''
    this.staticBackdrop = urlParams.get('static-backdrop') == 'true'
    this.searchQuery = urlParams.get('search-query') || ''
    this.searchSortBy = urlParams.get('search-sort-by') || ''
    this.searchSortByOpts = urlParams.get('search-sort-by-opts') || ''
    this.showSplash = urlParams.get('show-splash') == 'true'
    this.brandId = urlParams.get('brand-id') || ''
    this.portalId = urlParams.get('portal-id') || ''
    this.endpointId = urlParams.get('endpoint-id') || ''
    this.sdkMode = urlParams.get('sdk-mode') || SDKMode.None
    this.connectMode = urlParams.get('connect-mode') || ConnectMode.Popup
    this.theme = this.normalizeTheme(urlParams.get('theme'))
    this.applyTheme(this.theme)
    this.accentColor = this.normalizeAccentColor(urlParams.get('accent-color'))
    this.applyAccentColor(this.accentColor)
    this.logoUrl = urlParams.get('logo-url')?.trim() || ''
    this.companyName = urlParams.get('company-name')?.trim() || ''

    this.idpCode = urlParams.get('code') || ''
    this.idpState = urlParams.get('state') || ''
    this.idpError = urlParams.get('error') || ''
    this.idpErrorDescription = urlParams.get('error_description') || ''
    this.idpErrorUri = urlParams.get('error_uri') || ''

    //if neither searchOnly nor tefcaMode are set, default to searchOnly=true
    if (!this.searchOnly && !this.tefcaMode) {
      this.searchOnly = true
    }
  }

  public loading: boolean = true
  public errorMessage: string

  constructor(
    private activatedRoute: ActivatedRoute,
    public configService: ConfigService,
    public messageBus: MessageBusService,
    private fastenService: FastenService,
    private authService: AuthService,
    private router: Router,
    private logger: NGXLogger,
    private sentryContext: SentryContextService,
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.updateHeaderConfig());
    this.updateHeaderConfig();

    this.logger.info("QUERY STRING MAP", new URLSearchParams(window.location.search));
    this.populateInputsFromWindowLocation()

    this.messageBus.messageBusSubject.subscribe((eventPayload) => {
      this.logger.debug("bubbling up client-event", eventPayload)
      this.sendPostMessage(eventPayload)
    })

    if(this.isIdentityCallbackRequest()){
      this.restoreIdentityCallbackConfiguration()
      // OAuth state may restore the embed context. Publish it before logging the
      // transition, and never put callback credentials into application logs.
      this.logger.info("state: auth/callback")
      this.loading = false
      this.errorMessage = ""

      this.router.navigate(["auth/callback"], {
        queryParams: {
          code: this.idpCode || undefined,
          state: this.idpState || undefined,
          error: this.idpError || undefined,
          error_description: this.idpErrorDescription || undefined,
          error_uri: this.idpErrorUri || undefined,
        }
      })
      return
    }

    const apiMode = this.inferApiMode(this.publicId)

    //update the system config
    let eventTypes: EventTypes[] = []
    if(this.eventTypes){
      //split the event types by comma and trim any whitespace
      eventTypes = this.eventTypes.split(',').map((eventType) => {
        return eventType as EventTypes
      }).filter((eventType) => Object.values(EventTypes).indexOf(eventType) >= 0);
    }
    if(!this.sdkMode){
      this.sdkMode = SDKMode.None; //default to None if not set
    }

    this.configService.systemConfig = {
      apiMode: apiMode,
      publicId: this.publicId,
      org: undefined,
      externalId: this.externalId,
      externalState: this.externalState,
      reconnectOrgConnectionId: this.reconnectOrgConnectionId,
      staticBackdrop: this.staticBackdrop,
      searchOnly: this.searchOnly,
      showSplash: this.showSplash,
      tefcaMode: this.tefcaMode,
      tefcaCspPromptForce: this.tefcaCspPromptForce,
      identityRequestUri: this.tefcaMode ? this.identityRequestUri : '',
      eventTypes: eventTypes,
      sdkMode: this.sdkMode as SDKMode,
      connectMode: this.connectMode as ConnectMode,
      theme: this.theme,
      accentColor: this.accentColor || undefined,
      logoUrl: this.logoUrl || undefined,
      companyName: this.companyName || undefined,
    }
    this.configService.vaultProfileConfig = {
      email: this.email,
    }

    // Publish the parsed embed context before emitting any widget telemetry.
    // Keep the started event ahead of validation, which may fail synchronously.
    this.sentryContext.recordWidgetStarted(this.getWidgetExperience(), apiMode)
    this.getApiModeFromPublicId(this.publicId)

    //handle all the configuration options, via the inputs
    if(this.reconnectOrgConnectionId){
      this.fastenService.getOrgConnectionById(this.publicId, this.reconnectOrgConnectionId).subscribe((orgConnection) => {
        this.logger.info("state: dashboard/connecting#reconnectOrgConnectionId", orgConnection)

        // Navigate to dashboard/connecting WITHOUT changing URL
        // This preserves the security context so Safari doesn't block the popup
        this.router.navigate(["dashboard/connecting"], {
          queryParams: {
            "brandId": orgConnection.catalog_brand_id,
            "portalId": orgConnection.catalog_portal_id,
            "endpointId": orgConnection.catalog_endpoint_id,
            "orgConnectionId": orgConnection.org_connection_id,
            "externalId": this.externalId,
            "externalState": this.externalState,
            "sdkMode": this.sdkMode
          },
          skipLocationChange: true  // navigate without changing URL to preserve security context
        })

      }, (err) => {
        this.errorMessage = 'Could not find the patient connection using id. Please contact the developer of this app.'
        this.logger.error("Invalid Fasten Connect Connection ID", err)
      })
    } else if(this.brandId && this.portalId && this.endpointId) {
      this.logger.info("state: dashboard/connecting")
      //connect directly if the brandId, portalId and endpointId are provided. This is NOT a reconnect, just a shortcut for customers.
      this.router.navigate(["dashboard/connecting"], {
        queryParams: {
          "brandId": this.brandId,
          "portalId": this.portalId,
          "endpointId": this.endpointId,
          "externalId": this.externalId,
          "externalState": this.externalState,
          "sdkMode": this.sdkMode
        }
      })
    } else if(this.brandId){
      this.fastenService.searchCatalogBrand(apiMode, this.brandId).subscribe((brandItem) => {
        this.logger.info("state: brand/details")

        this.configService.searchConfig$.selectedBrand = brandItem._source
        this.router.navigateByUrl('brand/details')
      })


    } else {
      if(this.searchOnly){
        if(this.showSplash){
          this.logger.info("state: splash")
          this.router.navigateByUrl('splash')
        } else {
          this.logger.info("state: search")
          let searchFilter = new SearchFilter()
          if(this.searchQuery){
            searchFilter.query = this.searchQuery
          }
          if(this.searchSortBy){
            searchFilter.sortBy = this.searchSortBy
            if(this.searchSortByOpts){
              try {
                let base64DecodedSearchSortByOpts = Base64UrlDecode(this.searchSortByOpts)
                let sortByOptsObj = JSON.parse(base64DecodedSearchSortByOpts)
                if(searchFilter.sortBy == 'location' && sortByOptsObj.location && sortByOptsObj.location.zipcodes){
                  searchFilter.sortByOpts.locationZipcodes = sortByOptsObj.location.zipcodes
                }
              } catch (e) {
                this.logger.error("Could not parse searchSortByOpts", this.searchSortByOpts, e)
              }
            }
          }


          this.configService.searchConfig = {
            ...this.configService.searchConfig$,
            searchFilter: searchFilter
          }

          this.router.navigate(["search"])
        }
      }
    }

    if(this.searchSortBy == 'location'){
      //enable search by location
      this.configService.searchConfig$.showSearchByLocation = true
      this.configService.searchConfig$.searchFilter



    }

    //TODO: figure out how to show the modal after everything has loaded correctly:
    // currently the error page flashes quickly before the login screen is shown, either because the URL changes or because the
    // org data is still being requested. Previously we only showed the modal after the data was loaded.
    // this.stitchModal.nativeElement.showModal()
    // this.registerDialogCloseOnBackdropClick()
  }

  ngOnChanges(changes: any) {
    this.logger.debug("embed ngOnChanges", changes)
    if (changes.theme) {
      this.theme = this.normalizeTheme(changes.theme.currentValue)
      this.applyTheme(this.theme)
    }
    if (changes.accentColor) {
      this.accentColor = this.normalizeAccentColor(changes.accentColor.currentValue)
      this.applyAccentColor(this.accentColor)
    }
    //check if we need to debounce the changes to limit flashes.
  }

  private normalizeTheme(theme?: string | null): FdkTheme {
    return theme === FdkTheme.Dark ? FdkTheme.Dark : FdkTheme.Light
  }

  private applyTheme(theme: FdkTheme): void {
    document.documentElement.setAttribute('data-fdk-theme', theme)
  }

  private normalizeAccentColor(value?: string | null): string {
    const color = value?.trim() || ''
    const shortHexMatch = /^#([0-9a-f]{3})$/i.exec(color)

    if (shortHexMatch) {
      return `#${[...shortHexMatch[1]]
        .map((character) => character.repeat(2))
        .join('')}`.toUpperCase()
    }

    return /^#[0-9a-f]{6}$/i.test(color) ? color.toUpperCase() : ''
  }

  private applyAccentColor(value?: string | null): void {
    const root = document.documentElement
    const color = this.normalizeAccentColor(value)

    if (!color) {
      root.style.removeProperty('--fdk-color-accent')
      root.style.removeProperty('--fdk-color-accent-subtle')
      root.style.removeProperty('--fdk-color-accent-medium')
      return
    }

    root.style.setProperty('--fdk-color-accent', color)
    root.style.setProperty('--fdk-color-accent-subtle', this.hexToRgba(color, 0.08))
    root.style.setProperty('--fdk-color-accent-medium', this.hexToRgba(color, 0.16))
  }

  private hexToRgba(hex: string, opacity: number): string {
    const red = Number.parseInt(hex.slice(1, 3), 16)
    const green = Number.parseInt(hex.slice(3, 5), 16)
    const blue = Number.parseInt(hex.slice(5, 7), 16)

    return `rgb(${red} ${green} ${blue} / ${opacity})`
  }

  private isIdentityCallbackRequest(): boolean {
    return !!(this.idpCode && this.idpState) || !!this.idpError
  }

  private restoreIdentityCallbackConfiguration(): void {
    if(!this.idpState){
      return
    }

    const oauthContext = getOAuthCallbackContext(this.idpState, this.logger)
    if(oauthContext.systemConfig?.publicId){
      restoreConfigurationFromOauthContext(oauthContext, this.configService)
      this.theme = this.normalizeTheme(this.configService.systemConfig$.theme)
      this.applyTheme(this.theme)
      this.accentColor = this.normalizeAccentColor(
        this.configService.systemConfig$.accentColor,
      )
      this.applyAccentColor(this.accentColor)
      this.logoUrl = this.configService.systemConfig$.logoUrl || ''
      this.companyName = this.configService.systemConfig$.companyName || ''
    }
  }

  getApiModeFromPublicId(publicId: string): ApiMode {
    let publicIdParts = publicId.split('_')
    let apiMode = this.inferApiMode(publicId)
    if(publicIdParts.length != 3 ){
      console.error("Could not register Fasten Connect installation: missing or invalid id", this.publicId)
      this.errorMessage = 'Could not register Fasten Connect installation: missing or invalid id. Please contact the developer of this app.'
      this.sentryContext.recordWidgetConfigError('invalid_public_id')
      this.messageBus.publishWidgetConfigError()

      //reset the org setting if set
      this.configService.systemConfig = {
        org: undefined
      }
      this.loading = false

      return apiMode
    } else {
      this.errorMessage = ""

      this.fastenService.getOrgConfig(this.publicId).subscribe((org: Organization) => {
        this.logger.info("Fasten Connect registration", org)
        this.configService.systemConfig = {
          org: org
        }

        //ensure that TEFCA mode is only enabled for orgs that have the feature flag enabled
        if(this.configService.systemConfig$.tefcaMode && !this.isFeatureFlagOrgTefcaModeEnabled(this.configService.systemConfig$.org)) {
          this.logger.error("TEFCA mode requested but organization is not enabled for TEFCA", this.configService.systemConfig$.org?.feature_flags)
          this.loading = false
          this.errorMessage = 'TEFCA mode not enabled for this organization and/or api mode. Please contact your account representative or the developer of this app.'
          this.sentryContext.recordWidgetConfigError('tefca_not_enabled')
          this.messageBus.publishWidgetConfigError()
          return
        }

        // Keep sign-in unmounted until the server checks the probe cookies.
        if(this.tefcaMode) {
          void this.validateCookieSupport()
        } else {
          this.loading = false
          this.sentryContext.recordWidgetInitialized('cookie_not_required')
        }

      },(err) => {
        this.loading = false
        this.errorMessage = 'Could not register Fasten Connect installation using id. Please contact the developer of this app.'
        this.sentryContext.recordWidgetConfigError('org_config_failed')
        this.messageBus.publishWidgetConfigError()

        this.logger.error("Invalid Fasten Connect registration", err)
      })

      return apiMode
    }
  }

  private inferApiMode(publicId: string): ApiMode {
    const publicIdParts = publicId.split('_')
    return publicIdParts.length === 3 && publicIdParts[1] === ApiMode.Live
      ? ApiMode.Live
      : ApiMode.Test
  }

  private getWidgetExperience(): WidgetExperience {
    if (this.tefcaMode) {
      return 'tefca'
    }
    if (this.reconnectOrgConnectionId || (this.brandId && this.portalId && this.endpointId)) {
      return 'direct'
    }
    return 'search_only'
  }

  private async validateCookieSupport(): Promise<void> {
    try {
      const cookieProbe = isNativeSdkMode(this.sdkMode) ? CookieProbeScope.Regular : CookieProbeScope.All
      const cookieSupported = await this.authService.CheckCookieSupport(cookieProbe)
      if (cookieSupported) {
        this.logger.info('[AppComponent] Cookie support detected')
        this.sentryContext.recordWidgetInitialized('cookies_supported')
        return
      }

      if (this.authService.CanUseStorageAccessFallback()) {
        this.logger.info('[AppComponent] Cookie probes were blocked; continuing with the Storage Access API fallback')
        this.sentryContext.recordWidgetInitialized('storage_access_fallback')
      } else {
        this.logger.info('[AppComponent] Cookie support was not found!')
        this.sentryContext.recordWidgetBlocked('cookies_blocked')
        await this.router.navigateByUrl('auth/signin/cookies-required')
      }
    } catch (error) {
      this.logger.error('[AppComponent] Failed to check browser cookie support', error)
      this.errorMessage = 'Could not verify browser cookie support. Please try again or contact the developer of this app.'
      this.sentryContext.recordWidgetConfigError('cookie_probe_failed')
      this.messageBus.publishWidgetConfigError()
    } finally {
      this.loading = false
    }
  }



  // these functions can be called externally to hide the widget via javascript

  modalClose(): void {
    this.logger.info("modalClose pressed")
    //TODO: since we're in the iframe, we need to bubble up a custom event to the parent page to close the modal.
    this.messageBus.publishRequestClose()
  }

  handleHeaderBack(): void {
    if (this.headerConfig.backUrl) {
      void this.router.navigateByUrl(this.headerConfig.backUrl, {
        replaceUrl: this.headerConfig.backReplaceUrl === true,
      })
    }
  }

  handleHeaderReportIssue(): void {
    const queryParams = Object.entries(
      this.headerConfig.reportIssueQueryParamMap || {},
    ).reduce<Record<string, string>>((params, [source, target]) => {
      const value = this.activeHeaderRoute?.snapshot.queryParamMap.get(source)
      if (value) {
        params[target] = value
      }
      return params
    }, {})

    void this.router.navigate(['/form/support'], {
      queryParams: Object.keys(queryParams).length ? queryParams : undefined,
    })
  }

  handleHeaderProfile(): void {
    void this.router.navigateByUrl('/profile')
  }

  handleHeaderClose(): void {
    this.closeConfirmationOpen = true
  }

  cancelHeaderClose(): void {
    this.closeConfirmationOpen = false
  }

  confirmHeaderClose(): void {
    this.closeConfirmationOpen = false
    if (this.headerConfig.closeAction === 'complete-and-close') {
      this.messageBus.publishComplete()
    }
    this.modalClose()
  }

  shouldShowHeaderBackButton(): boolean {
    const hiddenByQueryParam = this.headerConfig.hideBackWhenQueryParam &&
      this.activeHeaderRoute?.snapshot.queryParamMap.has(
        this.headerConfig.hideBackWhenQueryParam,
      )

    return !!this.headerConfig.showBackButton && !hiddenByQueryParam
  }

  private updateHeaderConfig(): void {
    let route = this.activatedRoute
    while (route.firstChild) {
      route = route.firstChild
    }

    this.activeHeaderRoute = route
    this.headerConfig = {
      ...DEFAULT_EMBED_HEADER_CONFIG,
      ...(route.snapshot.data['header'] as EmbedHeaderConfig | undefined),
    }
  }

  // postMessage registration, listen to events from the parent window
  @HostListener('window:message', ['$event'])
  receivePostMessage(event: MessageEvent) {
    //TODO: SECURITY: change to the parent origin to be more secure (whitelisted in the app/organization)

    // this.receivePostMessage(event);
    this.logger.debug("received client-event from parent window", event)
    // this.sendPostMessage({api_mode: "test", event_type: EventTypes.EventTypeWidgetComplete, data: "response from child"} as EventPayload)
  }

  sendPostMessage(eventPayload: MessageBusEventPayload | null): void {
    if (eventPayload == null) {
      this.logger.warn("No eventPayload to send")
      return
    }

    if(this.sdkMode == SDKMode.ReactNative && window.ReactNativeWebView){
      this.logger.info("sending client-event to React Native WebView", eventPayload)
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          "from": CommunicationEntity.PrimaryWebView,
          "to": CommunicationEntity.External,
          "payload": JSON.stringify(eventPayload)
        })
      )

    } else if (window.opener || window.parent){
      this.logger.info("sending client-event", eventPayload)

      //TODO: should we filter out any widget.* events?
      //NOTE: widget.close MUST be sent to the parent window, because they must close the widget modal .

      let parentWindowRef = window.parent || window.opener
      //TODO: SECURITY: change to the parent origin to be more secure (whitelisted in the app)
      parentWindowRef.postMessage(JSON.stringify(eventPayload), "*")
    } else {
      this.logger.debug("No parent window to send message to", this.sdkMode)
      return
    }
  }

  private isFeatureFlagOrgTefcaModeEnabled(org?: Organization): boolean {
    const orgFlags = org?.feature_flags || []
    return orgFlags.includes(`${this.configService.systemConfig$.apiMode}.tefca.enable`)
  }
}
